<template>
  <div class="container-fluid first-view">
    <StudiesUploadProcessBar
      :show-modal="showModal"
      :upload-percent-progress="uploadPercentProgress"
      :upload-progress="currentProgress"
      :error-status="errorStatus"
      :error-message="errorMessage"
      :go-to-details-active="goToDetailsFlag"
      :go-to-details="goToFileProcessDetails"
      @close="() => showModal = false"
    />
    <div class="background-overlay"></div>
    <img class="logo" :src="logo" />
    <div class="studies-upload-body">
      <StudiesUploadFirstStep
        :status="currentStatus"
        :file-names="fileNames"
        :file-list="fileList"
        :years-to-pick="yearsToPick"
        :clients-to-pick="clientsToPick"
        :regions-to-pick="regionsToPick"
        :form-data="formData"
        @update:formData="formData = $event"
        :file-process-list="fileProcessList"
        :go-to-file-process-details="goToFileProcessDetails"
        :handle-change="handleChange"
        :handle-submit-files="submitSubmit"
        :handle-clear-files="handleClearFiles"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { notify } from '../services/notify'

import logo from '../assets/images/logos/Iris-Grey.svg'

import StudiesUploadFirstStep from '../components/StudiesUpload/StudiesUploadFirstStep'
import StudiesUploadProcessBar from '../components/StudiesUpload/StudiesUploadProcessBar'
import studyActions from '../store/modules/studies/actionTypes'
import Task from '@/helpers/task'

export default {
  name: 'StudiesUpload',
  components: {
    StudiesUploadFirstStep,
    StudiesUploadProcessBar
  },
  data () {
    return {
      logo,
      showModal: false,
      errorStatus: false,
      errorMessage: '',
      goToDetailsFlag: false,
      fileUpload: {},
      fileList: [],
      yearsToPick: [],
      clientsToPick: [],
      regionsToPick: [],
      studyGroup: [],
      studyFiles: {},
      uploadPercentProgress: 0,
      alert: {
        type: '',
        icon: '',
        text: '',
        isVisible: false
      },
      formData: {
        client: '',
        study_type: '',
        region: '',
        year: ''
      },
      uploadProgress: {
        UPLOADING: { text: 'Uploading data on the server', status: 'upload' },
        VALIDATION: { text: 'Validation and data injecting', status: 'validation' },
        DONE: { text: 'Done', status: 'done' },
        FAILED: { text: 'Failed', status: 'failed' }
      },
      currentProgress: {},
      uploadStatus: {
        NORMAL: { background: '#3B86FF', icon: 'cloud-upload-alt', text: '' },
        SUCCESS: { background: '#379947', icon: 'cloud', text: 'Uploaded' },
        WARNING: { background: '#DA9251', icon: 'cloud', text: 'Selected' },
        FAILED: { background: '#BB121A', icon: 'cloud', text: 'Failed' },
        UPLOADING: { background: '#DA9251', icon: 'circle-notch', text: 'Uploading' }
      },
      currentStatus: {},
      file: {},
      fileProcessId: '',
      fileProcessList: []
    }
  },
  async mounted () {
    this.setCurrentStatus('queued')
    this.changeUploadProcess('uploading')
    await this.getYearsArrayFromToday()
    await this.getRequireData()
    await this.fetchStudiesHistory()
  },
  computed: {
    fileNames () {
      if (!this.fileList.length) return ''
      return this.fileList.map(file => file.name).join(',') + ` ${this.currentStatus.text}`
    }
  },
  methods: {
    ...mapActions({
      uploadStudies: studyActions.UPLOAD_STUDIES,
      getUpdatedDropdownValues: studyActions.UPDATE_DROPDOWN_OPTIONS,
      getFileProcessList: studyActions.FETCH_FILE_PROCESS
    }),
    setUploadPercentProgress (value) {
      this.uploadPercentProgress = value
    },
    validFormData () {
      let validForm = true
      const errors = []
      Object.keys(this.formData).forEach(key => {
        if (key !== 'study_type') {
          const checkIfEmpty = this.formData[key] === ''
          if (checkIfEmpty) {
            errors.push(`Empty ${key}`)
            validForm = false
          }
        }
      })
      if (errors.length) {
        notify({
          title: 'Missing required fields',
          message: errors.join(', ') + '. Please fill all data!',
          type: 'warning'
        })
      }
      return validForm
    },
    handleClearFiles () {
      this.fileList = []
      this.setCurrentStatus('queued')
    },
    changeUploadProcess (progress) {
      switch (progress) {
        case 'uploading':
          this.currentProgress = this.uploadProgress.UPLOADING
          break
        case 'validation':
          this.currentProgress = this.uploadProgress.VALIDATION
          break
        case 'done':
          this.currentProgress = this.uploadProgress.DONE
          break
        case 'failed':
          this.currentProgress = this.uploadProgress.FAILED
          break
        default:
          this.currentProgress = this.uploadProgress.UPLOADING
      }
    },
    setCurrentStatus (status) {
      switch (status) {
        case 'queued':
          this.currentStatus = this.uploadStatus.NORMAL
          break
        case 'selected':
          this.currentStatus = this.uploadStatus.WARNING
          break
        case 'started':
          this.currentStatus = this.uploadStatus.UPLOADING
          break
        case 'failed':
          this.currentStatus = this.uploadStatus.FAILED
          break
        case 'success':
          this.currentStatus = this.uploadStatus.SUCCESS
          break
        default:
          this.currentStatus = this.uploadStatus.NORMAL
      }
    },
    handleChange (file) {
      switch (file.status) {
        case 'ready':
          this.fileUpload = {
            filename: file.name,
            file: file,
            status: 'selected'
          }
          this.fileList = [file.raw]
          this.setCurrentStatus(this.fileUpload.status)
          break
        case 'fail':
          this.fileUpload = {
            filename: file.name,
            file: file,
            status: 'failed'
          }
          this.setCurrentStatus(this.fileUpload.status)
          break
      }
    },
    async getYearsArrayFromToday () {
      const today = new Date()
      const currentYear = today.getFullYear()

      for (let i = 0; i <= 5; i++) this.yearsToPick.push(currentYear - i)
    },
    async getRequireData () {
      const res = await this.getUpdatedDropdownValues({ query: [] })
      const data = res.data.fields[0].data
      this.clientsToPick = data.find(obj => obj.field.toLowerCase() === 'client').options
      this.regionsToPick = data.find(obj => obj.field.toLowerCase() === 'region').options
    },
    async fetchStudiesHistory () {
      const data = await this.getFileProcessList() || []
      this.fileProcessList = data
    },
    async submitSubmit () {
      if (this.fileList.length === 0) return
      if (this.validFormData()) {
        this.uploadPercentProgress = 0
        this.setCurrentStatus('started')
        this.changeUploadProcess('uploading')
        this.errorStatus = false
        this.showModal = true
        for (const file of this.fileList) {
          const data = await this.uploadStudies({
            file,
            form: this.formData,
            onProgress: this.setUploadPercentProgress
          })
          if (!data) {
            this.errorMessage = 'Failed to send studies'
            this.errorStatus = true
            this.changeUploadProcess('failed')
            return
          }
          const taskId = data['task_id']
          this.fileProcessId = data['file_process_id']
          const url = `/fileprocess/${taskId}/status`
          this.changeUploadProcess('validation')
          const task = new Task(url)
          task.on('result', (result) => {
            this.setCurrentStatus('success')
            this.changeUploadProcess('done')
            this.fetchStudiesHistory()
          })
          task.on('error', (error) => {
            this.errorMessage = error.message ? error.message : error
            this.changeUploadProcess('failed')
            this.setCurrentStatus('failed')
            this.errorStatus = true
            this.goToDetailsFlag = true
            this.fetchStudiesHistory()
          })
        }
      }
    },
    goToFileProcessDetails (fileProcessId = null) {
      if (fileProcessId) this.fileProcessId = fileProcessId
      this.$router.push({ name: 'studies-upload-details', params: { id: this.fileProcessId } })
    }
  }
}
</script>

<style scoped lang="scss">
.container-fluid.first-view {
  padding: 99px 87px !important;
  background-image: url("../assets/images/backgrounds/IRIS-background-720p.jpg");
  background-position: center;
  background-size: cover;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  position: relative;
  min-height: 100vh;
  overflow-y: auto;
  .background-overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: #2a274b;
    opacity: 0.4;
    z-index: 0;
    pointer-events: none;
  }
}

.studies-upload-body {
  position: relative;
  z-index: 2;
  width: 100%;
  display: flex;
  justify-content: center;
}

.logo {
  position: fixed;
  bottom: 20px;
  right: 50px;
  width: 6.5%;
  height: auto;
}

.container-fluid {
  height: 100%;
}
</style>
